import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Step 1: Basic Information
  campaignName: "",
  country: "United States",
  stateRegion: "",
  postalCode: "",
  estimatedStartDate: "",
  estimatedEndDate: "",
  isOpenCampaign: "Yes",
  contributionType: "Both",
  payoutPreference: "Either",
  isSuperCampaign: "No",
  associatedSuperCampaign: "",

  // Step 2: Description and Uploads
  description: "",
  profileImage: null, // Preview URL for display
  profileImageFile: null, // Actual File object for upload
  coverImage: null, // Preview URL for display
  coverImageFile: null, // Actual File object for upload

  // Step 3: Impact
  projectNeed: "",
  category: "",
  sdg: "",
  peopleImpact: "",
  planetImpact: "",

  // Step 4: Transparency (was Step 5)
  teamMembers: [],
  reportActivity: false,
  shareLessons: false,

  // Step 5: Feasibility (was Step 6)
  operationalCapacity: "",
  riskFactors: "",

  // Step 6: References (was Step 7)
  socialMedia: {
    instagram: "",
    linkedin: "",
    tiktok: "",
    facebook: "",
  },
  personalReference: {
    name: "",
    email: "",
    phone: "",
    relation: "",
  },
  professionalReference: {
    name: "",
    email: "",
    phone: "",
    relation: "",
  },

  // Step completion tracking
  stepsCompleted: {
    step1: false,
    step2: false,
    step3: false,
    step4: false,
    step5: false,
    step6: false,
  },

  // Internal state management
  currentStep: 1,
  isSubmitting: false,
  lastSavedAt: null,
};

// Validation functions for each step
export const validateStep1 = (state) => {
  return !!(
    state.campaignName?.trim() &&
    state.country?.trim() &&
    state.stateRegion?.trim() &&
    state.postalCode?.trim() &&
    state.estimatedStartDate &&
    state.estimatedEndDate
  );
};

export const validateStep2 = (state) => {
  return !!state.description?.trim();
};

export const validateStep3 = (state) => {
  return !!(
    state.projectNeed?.trim() &&
    state.category?.trim() &&
    state.sdg?.trim() &&
    state.peopleImpact?.trim() &&
    state.planetImpact?.trim()
  );
};

export const validateStep4 = (state) => {
  return (
    state.teamMembers?.length > 0 &&
    state.teamMembers.every((m) => m.name?.trim() && m.email?.trim())
  );
};

export const validateStep5 = (state) => {
  return !!(state.operationalCapacity?.trim() && state.riskFactors?.trim());
};

export const validateStep6 = (state) => {
  const hasSocialMedia =
    state.socialMedia?.instagram?.trim() ||
    state.socialMedia?.linkedin?.trim() ||
    state.socialMedia?.tiktok?.trim() ||
    state.socialMedia?.facebook?.trim();
  const hasPersonalRef =
    state.personalReference?.name?.trim() && state.personalReference?.email?.trim();
  const hasProfessionalRef =
    state.professionalReference?.name?.trim() && state.professionalReference?.email?.trim();
  return !!(hasSocialMedia || hasPersonalRef || hasProfessionalRef);
};

export const getStepValidator = (stepNumber) => {
  const validators = {
    1: validateStep1,
    2: validateStep2,
    3: validateStep3,
    4: validateStep4,
    5: validateStep5,
    6: validateStep6,
  };
  return validators[stepNumber] || (() => false);
};

// Load initial state from localStorage if available
const loadFromLocalStorage = () => {
  if (typeof window !== "undefined") {
    try {
      const savedData = localStorage.getItem("campaignFormData");
      if (savedData) {
        const parsed = JSON.parse(savedData);
        return { ...initialState, ...parsed };
      }
    } catch (error) {
      console.warn("Failed to load campaign data from localStorage:", error);
    }
  }
  return initialState;
};

// Save to localStorage
const saveToLocalStorage = (state) => {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem("campaignFormData", JSON.stringify(state));
    } catch (error) {
      console.warn("Failed to save campaign data to localStorage:", error);
    }
  }
};

export const campaignSlice = createSlice({
  name: "campaign",
  initialState: loadFromLocalStorage(),
  reducers: {
    updateCampaignData: (state, action) => {
      const newState = {
        ...state,
        ...action.payload,
        lastSavedAt: new Date().toISOString(),
      };
      saveToLocalStorage(newState);
      return newState;
    },

    updateStep: (state, action) => {
      state.currentStep = action.payload;
      saveToLocalStorage(state);
    },

    setSubmitting: (state, action) => {
      state.isSubmitting = action.payload;
      saveToLocalStorage(state);
    },

    addTeamMember: (state, action) => {
      state.teamMembers.push(
        action.payload || {
          name: "",
          email: "",
          phone: "",
        },
      );
      saveToLocalStorage(state);
    },

    updateTeamMember: (state, action) => {
      const { index, data } = action.payload;
      if (state.teamMembers[index]) {
        state.teamMembers[index] = { ...state.teamMembers[index], ...data };
        saveToLocalStorage(state);
      }
    },

    removeTeamMember: (state, action) => {
      const index = action.payload;
      state.teamMembers.splice(index, 1);
      saveToLocalStorage(state);
    },

    resetCampaignData: () => {
      if (typeof window !== "undefined") {
        localStorage.removeItem("campaignFormData");
      }
      return initialState;
    },

    clearLocalStorage: () => {
      if (typeof window !== "undefined") {
        localStorage.removeItem("campaignFormData");
      }
    },

    markStepComplete: (state, action) => {
      const stepNumber = action.payload;
      state.stepsCompleted[`step${stepNumber}`] = true;
      saveToLocalStorage(state);
    },

    markStepIncomplete: (state, action) => {
      const stepNumber = action.payload;
      state.stepsCompleted[`step${stepNumber}`] = false;
      saveToLocalStorage(state);
    },
  },
});

export const {
  updateCampaignData,
  updateStep,
  setSubmitting,
  addTeamMember,
  updateTeamMember,
  removeTeamMember,
  resetCampaignData,
  clearLocalStorage,
  markStepComplete,
  markStepIncomplete,
} = campaignSlice.actions;

// Helper to check if user can access a given step
export const canAccessStep = (state, targetStep) => {
  if (targetStep === 1) return true;
  for (let i = 1; i < targetStep; i++) {
    if (!state.stepsCompleted[`step${i}`]) {
      return false;
    }
  }
  return true;
};

// Helper to get first incomplete step
export const getFirstIncompleteStep = (state) => {
  for (let i = 1; i <= 6; i++) {
    if (!state.stepsCompleted[`step${i}`]) {
      return i;
    }
  }
  return 7; // all steps complete → review
};

export default campaignSlice.reducer;
